import React from 'react';
import { Navigation, Route, Plus, Bookmark, Search, Share2, X, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MapContextMenuModalProps {
  coords: { latitude: number; longitude: number; name?: string };
  onClose: () => void;
  onDirectionsHere: () => void;
  onStartRouteHere: () => void;
  onAddAsStop?: () => void;
  onSaveSpot: () => void;
  onExploreNearby: () => void;
  onSharePosition: () => void;
}

const MapContextMenuModal: React.FC<MapContextMenuModalProps> = ({
  coords,
  onClose,
  onDirectionsHere,
  onStartRouteHere,
  onAddAsStop,
  onSaveSpot,
  onExploreNearby,
  onSharePosition,
}) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
              <MapPin size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-black text-[var(--text-primary)] truncate">
                {coords.name || `${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`}
              </h3>
              <p className="text-2xs font-bold text-[var(--text-muted)] truncate">
                {coords.latitude.toFixed(5)}° N, {coords.longitude.toFixed(5)}° Ø
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Menu Options */}
        <div className="p-2 space-y-1">
          <button
            onClick={onDirectionsHere}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-emerald-500/10 text-[var(--text-primary)] hover:text-emerald-500 transition-all group text-left"
          >
            <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] group-hover:bg-emerald-500/20 flex items-center justify-center text-[var(--text-secondary)] group-hover:text-emerald-500 transition-colors shrink-0 shadow-sm">
              <Navigation size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black truncate">{t('map.menu.directions_here', 'Veibeskrivelse hit')}</div>
              <div className="text-2xs font-semibold text-[var(--text-muted)] truncate">{t('map.menu.directions_sub', 'Planlegg rute til dette punktet')}</div>
            </div>
          </button>

          <button
            onClick={onStartRouteHere}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-blue-500/10 text-[var(--text-primary)] hover:text-blue-500 transition-all group text-left"
          >
            <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] group-hover:bg-blue-500/20 flex items-center justify-center text-[var(--text-secondary)] group-hover:text-blue-500 transition-colors shrink-0 shadow-sm">
              <Route size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black truncate">{t('map.menu.start_route_here', 'Start rute herfra')}</div>
              <div className="text-2xs font-semibold text-[var(--text-muted)] truncate">{t('map.menu.start_route_sub', 'Sett som avreisested for ruteplanlegger')}</div>
            </div>
          </button>

          {onAddAsStop && (
            <button
              onClick={onAddAsStop}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-amber-500/10 text-[var(--text-primary)] hover:text-amber-500 transition-all group text-left border-t border-[var(--border-primary)] pt-3"
            >
              <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] group-hover:bg-amber-500/20 flex items-center justify-center text-[var(--text-secondary)] group-hover:text-amber-500 transition-colors shrink-0 shadow-sm">
                <Plus size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate">{t('map.menu.add_stop', 'Legg til som stopp')}</div>
                <div className="text-2xs font-semibold text-[var(--text-muted)] truncate">{t('map.menu.add_stop_sub', 'Legg til i pågående ruteplan')}</div>
              </div>
            </button>
          )}

          <button
            onClick={onSaveSpot}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-violet-500/10 text-[var(--text-primary)] hover:text-violet-500 transition-all group text-left border-t border-[var(--border-primary)] pt-3"
          >
            <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] group-hover:bg-violet-500/20 flex items-center justify-center text-[var(--text-secondary)] group-hover:text-violet-500 transition-colors shrink-0 shadow-sm">
              <Bookmark size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black truncate">{t('map.menu.save_spot', 'Lagre sted / posisjon')}</div>
              <div className="text-2xs font-semibold text-[var(--text-muted)] truncate">{t('map.menu.save_spot_sub', 'Opprett en ny fricamp-plass eller favoritt')}</div>
            </div>
          </button>

          <button
            onClick={onExploreNearby}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-cyan-500/10 text-[var(--text-primary)] hover:text-cyan-500 transition-all group text-left"
          >
            <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] group-hover:bg-cyan-500/20 flex items-center justify-center text-[var(--text-secondary)] group-hover:text-cyan-500 transition-colors shrink-0 shadow-sm">
              <Search size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black truncate">{t('map.menu.explore_nearby', 'Utforsk i nærheten')}</div>
              <div className="text-2xs font-semibold text-[var(--text-muted)] truncate">{t('map.menu.explore_nearby_sub', 'Zoom inn og vis plasser i dette området')}</div>
            </div>
          </button>

          <button
            onClick={onSharePosition}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-emerald-500/10 text-[var(--text-primary)] hover:text-emerald-500 transition-all group text-left border-t border-[var(--border-primary)] pt-3"
          >
            <div className="w-8 h-8 rounded-xl bg-[var(--bg-surface-alt)] group-hover:bg-emerald-500/20 flex items-center justify-center text-[var(--text-secondary)] group-hover:text-emerald-500 transition-colors shrink-0 shadow-sm">
              <Share2 size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black truncate">{t('map.menu.share_pos', 'Del posisjon')}</div>
              <div className="text-2xs font-semibold text-[var(--text-muted)] truncate">{t('map.menu.share_pos_sub', 'Kopier kartlenke med eksakte koordinater')}</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapContextMenuModal;
